import React, { useState, useEffect, useContext } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";
import {
  ArrowLeft,
  ChevronRight,
  Copy,
  MapPin,
  Calendar,
  Clock,
  Car,
  User,
  ShieldCheck,
  CheckCircle2,
  ExternalLink,
  Star,
  Users,
  DollarSign,
  Zap,
  Check,
} from "lucide-react";

import profile_placeholder from "../assets/images/profile_placeholder.png";
import henceforthApi from "../utils/henceforthApi";
import henceofrthEnums from "../utils/henceofrthEnums";
import { GlobalContext } from "../context/Provider";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

interface RideDetail {
  _id?: string;
  seats_available?: number;
  total_seats?: number;
  price?: number;
  currency?: string;
  driver_id?: string;
  driver_name?: string;
  driver_image?: string;
  avg_rating?: number;
  total_reviews?: number;
  address_to?: string;
  address_from?: string;
  location_from?: {
    coordinates?: [number, number];
  };
  location_to?: {
    coordinates?: [number, number];
  };
  time_taken?: string;
  date?: string | number;
  pickup_time?: string;
  vehicle_brand_name?: string;
  vehicle_model_name?: string;
  vehicle_type_name?: string;
  vehicle_color_name?: string;
  image?: string;
  is_allow_instant_booking?: boolean;
}

function RideDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { authState, handleError } = useContext(GlobalContext);

  const [loading, setLoading] = useState<boolean>(true);
  const [ridedetail, setRidedetail] = useState<RideDetail | null>(null);
  const [copiedId, setCopiedId] = useState<boolean>(false);

  if (authState.access_token) {
    henceforthApi.setToken(authState.access_token);
  }

  const getRideDetails = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const res = await henceforthApi.RidingDetails.getRideDetails(id);
      if (res?.data && res.data.length > 0) {
        setRidedetail(res.data[0]);
      } else {
        setRidedetail(null);
      }
    } catch (error) {
      handleError(error);
      setRidedetail(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getRideDetails();
  }, [id]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(true);
    toast.success("Ride ID copied to clipboard!");
    setTimeout(() => setCopiedId(false), 2000);
  };

  const formatPrice = (price: number | undefined, currency?: string) => {
    if (price === undefined || price === null) return "N/A";
    const symbol =
      currency === henceofrthEnums.CurrencyType.NGN
        ? "₦ "
        : currency === henceofrthEnums.CurrencyType.INR
          ? "₹ "
          : "$ ";
    return `${symbol}${price.toLocaleString()}`;
  };

  const googleMapsUrl =
    ridedetail?.address_from && ridedetail?.address_to
      ? `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(
        ridedetail.address_from
      )}&destination=${encodeURIComponent(ridedetail.address_to)}`
      : null;

  // Loading skeleton state
  if (loading) {
    return (
      <div className="w-full max-w-[1600px] mx-auto space-y-6 pb-12 animate-pulse">
        <div className="h-5 bg-slate-200 rounded w-64 mb-4" />
        <div className="flex justify-between items-center pb-4 border-b border-slate-100">
          <div className="space-y-2">
            <div className="h-8 bg-slate-200 rounded w-48" />
            <div className="h-4 bg-slate-100 rounded w-72" />
          </div>
          <div className="h-9 bg-slate-200 rounded w-24" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-28 bg-white border border-slate-200 rounded-2xl p-4" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 h-96 bg-white border border-slate-200 rounded-2xl p-6" />
          <div className="h-96 bg-white border border-slate-200 rounded-2xl p-6" />
        </div>
      </div>
    );
  }

  // Empty state
  if (!ridedetail) {
    return (
      <div className="w-full max-w-[1600px] mx-auto py-16 text-center space-y-4">
        <div className="w-16 h-16 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center mx-auto">
          <Car className="w-8 h-8" />
        </div>
        <h2 className="text-xl font-bold text-slate-800">Ride Not Found</h2>
        <p className="text-sm text-slate-500 max-w-md mx-auto">
          The requested ride could not be retrieved. It may have been deleted or the ID is incorrect.
        </p>
        <Button
          type="button"
          onClick={() => navigate("/created-rides/1")}
          className="!bg-[#087F5B] hover:!bg-[#066749] text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Rides
        </Button>
      </div>
    );
  }

  const rideDateFormatted = ridedetail.date
    ? moment(Number(ridedetail.date)).format("DD MMM YYYY")
    : "Date not available";

  const totalSeats = ridedetail.total_seats ?? (ridedetail.seats_available ?? 0);
  const seatsAvailable = ridedetail.seats_available ?? 0;
  const isSoldOut = seatsAvailable === 0;

  return (
    <div className="w-full max-w-[1600px] mx-auto space-y-6 pb-12 font-sans">
      {/* ================= 3. TOP KEY METRICS CARDS ================= */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Price per seat */}
        <div className="bg-white rounded-2xl border border-slate-200/80 p-4 shadow-2xs flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
              Price Per Seat
            </span>
            <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 border border-emerald-100 flex items-center justify-center">
              <DollarSign className="w-4 h-4" />
            </div>
          </div>
          <div>
            <div className="text-2xl font-extrabold text-slate-900 leading-tight">
              {formatPrice(ridedetail.price, ridedetail.currency)}
            </div>
            <p className="text-xs text-slate-500 mt-1 mb-0">Base passenger fare</p>
          </div>
        </div>

        {/* Seats Availability */}
        <div className="bg-white rounded-2xl border border-slate-200/80 p-4 shadow-2xs flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
              Seats Available
            </span>
            <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 border border-blue-100 flex items-center justify-center">
              <Users className="w-4 h-4" />
            </div>
          </div>
          <div>
            <div className="text-2xl font-extrabold text-slate-900 leading-tight">
              {seatsAvailable} <span className="text-sm font-semibold text-slate-500">/ {totalSeats}</span>
            </div>
            <p className="text-xs text-slate-500 mt-1 mb-0">
              {isSoldOut ? "No remaining seats" : `${seatsAvailable} available for booking`}
            </p>
          </div>
        </div>

        {/* Ride Date */}
        <div className="bg-white rounded-2xl border border-slate-200/80 p-4 shadow-2xs flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
              Trip Date
            </span>
            <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 border border-indigo-100 flex items-center justify-center">
              <Calendar className="w-4 h-4" />
            </div>
          </div>
          <div>
            <div className="text-lg font-extrabold text-slate-900 leading-tight truncate" title={rideDateFormatted}>
              {rideDateFormatted}
            </div>
            <p className="text-xs text-slate-500 mt-1 mb-0">
              Departure: <strong className="text-slate-700">{ridedetail.pickup_time || "N/A"}</strong>
            </p>
          </div>
        </div>

        {/* Duration / ETA */}
        <div className="bg-white rounded-2xl border border-slate-200/80 p-4 shadow-2xs flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
              Estimated Duration
            </span>
            <div className="w-8 h-8 rounded-lg bg-sky-50 text-sky-600 border border-sky-100 flex items-center justify-center">
              <Clock className="w-4 h-4" />
            </div>
          </div>
          <div>
            <div className="text-lg font-extrabold text-slate-900 leading-tight truncate">
              {ridedetail.time_taken || "N/A"}
            </div>
            <p className="text-xs text-slate-500 mt-1 mb-0">Estimated travel time</p>
          </div>
        </div>
      </div>

      {/* ================= 4. MAIN DETAILS: ROUTE & DETAILS (2 COLS) vs DRIVER & VEHICLE (1 COL) ================= */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT / CENTER (2 COLS): ITINERARY & ROUTE */}
        <div className="lg:col-span-2 space-y-6">
          {/* Route & Timeline Card */}
          <div className="bg-white rounded-2xl border border-slate-200/80 p-4 sm:p-6 shadow-2xs">
            <div className="flex items-center justify-between pb-4 mb-4 border-b border-slate-100">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                  <MapPin className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900 m-0">Journey &amp; Itinerary</h3>
                  <p className="text-xs text-slate-500 m-0">Origin, destination, and schedule details</p>
                </div>
              </div>
            </div>

            {/* Visual Route Timeline */}
            <div className="space-y-4 pt-1">
              {/* Pickup / Origin */}
              <div className="flex items-start gap-3.5">
                <div className="flex flex-col items-center mt-1">
                  <div className="w-5 h-5 rounded-full bg-emerald-100 border-2 border-emerald-600 flex items-center justify-center shrink-0">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-bold text-emerald-700 uppercase tracking-wider">
                      Pickup Location (Origin)
                    </span>
                    <span className="text-xs font-semibold text-slate-700">
                      {ridedetail.pickup_time || "N/A"}
                    </span>
                  </div>
                  <p className="text-sm font-semibold text-slate-900 mt-0.5 mb-1 leading-snug">
                    {ridedetail.address_from || "Address not specified"}
                  </p>
                  <p className="text-xs text-slate-500 mb-0">
                    Departure Date: {rideDateFormatted}
                  </p>
                </div>
              </div>

              {/* Connecting Line with Distance / Duration */}
              <div className="ml-2.5 pl-5 border-l-2 border-dotted border-slate-200 py-2">
                <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-slate-50 border border-slate-200/80 text-xs text-slate-600">
                  <Clock className="w-3.5 h-3.5 text-slate-400" />
                  <span>Estimated Duration: <strong>{ridedetail.time_taken || "Direct Journey"}</strong></span>
                </div>
              </div>

              {/* Dropoff / Destination */}
              <div className="flex items-start gap-3.5">
                <div className="flex flex-col items-center mt-1">
                  <div className="w-5 h-5 rounded-full bg-rose-100 border-2 border-rose-600 flex items-center justify-center shrink-0">
                    <MapPin className="w-2.5 h-2.5 text-rose-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-bold text-rose-700 uppercase tracking-wider">
                      Drop-off Location (Destination)
                    </span>
                  </div>
                  <p className="text-sm font-semibold text-slate-900 mt-0.5 mb-1 leading-snug">
                    {ridedetail.address_to || "Address not specified"}
                  </p>
                  <p className="text-xs text-slate-500 mb-0">Final destination point</p>
                </div>
              </div>
            </div>
          </div>

          {/* Ride Settings & Information */}
          <div className="bg-white rounded-2xl border border-slate-200/80 p-4 sm:p-6 shadow-2xs">
            <div className="flex items-center justify-between pb-4 mb-4 border-b border-slate-100">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center">
                  <ShieldCheck className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900 m-0">Ride Policies &amp; Preferences</h3>
                  <p className="text-xs text-slate-500 m-0">Booking preferences and capacity limits</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/80">
                <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                  Booking Mode
                </span>
                <div className="flex items-center gap-2">
                  {ridedetail.is_allow_instant_booking ? (
                    <>
                      <Zap className="w-4 h-4 text-amber-500 fill-amber-500" />
                      <span className="text-sm font-bold text-slate-900">Instant Booking Enabled</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4 text-slate-500" />
                      <span className="text-sm font-bold text-slate-900">Manual Driver Approval</span>
                    </>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-1 mb-0">
                  {ridedetail.is_allow_instant_booking
                    ? "Riders can automatically confirm seats without driver pre-approval."
                    : "Riders must request to join and await confirmation by the driver."}
                </p>
              </div>

              <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/80">
                <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                  Seat Allocation
                </span>
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-emerald-600" />
                  <span className="text-sm font-bold text-slate-900">
                    {seatsAvailable} of {totalSeats} Seats Free
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-1 mb-0">
                  Capacity limit set by driver for comfortable carpooling.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN (1 COL): DRIVER & VEHICLE */}
        <div className="space-y-6">
          {/* Driver Profile Card */}
          <div className="bg-white rounded-2xl border border-slate-200/80 p-4 shadow-2xs">
            <div className="flex items-center justify-between pb-3 mb-4 border-b border-slate-100">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                Driver Details
              </span>
              {ridedetail.driver_id && (
                <Link
                  to={`/user/${ridedetail.driver_id}`}
                  className="text-xs font-semibold text-emerald-700 hover:text-emerald-800 hover:underline inline-flex items-center gap-0.5 no-underline"
                >
                  <span>Profile</span>
                  <ChevronRight className="w-3 h-3" />
                </Link>
              )}
            </div>

            <div className="flex items-center gap-4">
              <div className="relative">
                <img
                  src={
                    ridedetail.driver_image
                      ? henceforthApi.FILES.imageOriginal(ridedetail.driver_image)
                      : profile_placeholder
                  }
                  alt={ridedetail.driver_name || "Driver"}
                  className="w-14 h-14 rounded-full object-cover border-2 border-emerald-500/20 shadow-sm"
                  onError={(e: any) => {
                    e.currentTarget.src = profile_placeholder;
                  }}
                />
                <span className="absolute bottom-0 right-0 w-4 h-4 rounded-full bg-emerald-500 border-2 border-white flex items-center justify-center">
                  <Check className="w-2.5 h-2.5 text-white" />
                </span>
              </div>

              <div className="flex-1 min-w-0">
                <h4 className="text-base font-bold text-slate-900 m-0 truncate">
                  {ridedetail.driver_name || "Driver Name N/A"}
                </h4>

                {/* Rating & Reviews */}
                <div className="flex items-center gap-1.5 mt-1">
                  <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200/60 text-[11px] font-bold">
                    <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                    <span>{(ridedetail.avg_rating || 0).toFixed(1)}</span>
                  </div>
                  <span className="text-xs text-slate-400">
                    ({ridedetail.total_reviews || 0} reviews)
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
              <span>Driver Account:</span>
              <span className="font-semibold text-emerald-700">Verified Driver</span>
            </div>
          </div>

          {/* Vehicle Card */}
          <div className="bg-white rounded-2xl border border-slate-200/80 p-4 shadow-2xs">
            <div className="flex items-center justify-between pb-3 mb-4 border-b border-slate-100">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                Assigned Vehicle
              </span>
              <span className="text-xs font-semibold text-slate-600">
                {ridedetail.vehicle_type_name || "Standard Car"}
              </span>
            </div>

            {/* Vehicle Image */}
            {ridedetail.image && (
              <div className="w-full h-40 rounded-xl overflow-hidden bg-slate-100 mb-4 border border-slate-200/60 shadow-2xs">
                <img
                  src={henceforthApi.FILES.imageOriginal(ridedetail.image)}
                  alt={ridedetail.vehicle_brand_name || "Vehicle"}
                  className="w-full h-full object-cover"
                  onError={(e: any) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              </div>
            )}

            {/* Vehicle Specs Grid */}
            <div className="space-y-2.5 text-xs">
              <div className="flex items-center justify-between py-1 border-b border-slate-50">
                <span className="text-slate-500">Brand / Make:</span>
                <span className="font-bold text-slate-900">
                  {ridedetail.vehicle_brand_name || "Not specified"}
                </span>
              </div>

              <div className="flex items-center justify-between py-1 border-b border-slate-50">
                <span className="text-slate-500">Model:</span>
                <span className="font-bold text-slate-900">
                  {ridedetail.vehicle_model_name || "Not specified"}
                </span>
              </div>

              <div className="flex items-center justify-between py-1 border-b border-slate-50">
                <span className="text-slate-500">Body Type:</span>
                <span className="font-semibold text-slate-800">
                  {ridedetail.vehicle_type_name || "Sedan / Hatchback"}
                </span>
              </div>

              <div className="flex items-center justify-between py-1 border-b border-slate-50">
                <span className="text-slate-500">Color:</span>
                <div className="flex items-center gap-1.5">
                  <span
                    className="w-2.5 h-2.5 rounded-full border border-slate-300"
                    style={{
                      backgroundColor:
                        ridedetail.vehicle_color_name?.toLowerCase() || "#cbd5e1",
                    }}
                  />
                  <span className="font-semibold text-slate-800 capitalize">
                    {ridedetail.vehicle_color_name || "Not specified"}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between py-1">
                <span className="text-slate-500">Passenger Seats:</span>
                <span className="font-bold text-emerald-700">
                  {totalSeats} Seats Total
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RideDetails;
